import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Typography,
} from 'antd';
import type { AgentClient } from '../agentClient';
import { LIVE_MOVED_TEXT, toSafeMessage } from '../agentClient';
import {
  afterSave,
  copyWarning,
  draftBehind,
  editorButtons,
  editorSource,
  errorToShow,
  lineDiff,
  localSaveRefusal,
  oneLineOk,
  otherDrafts,
  refusalText,
  SKILLS_STALE_TEXT,
  skillHint,
} from '../skillEditView';
import {
  editorText,
  skillKey,
  withEdit,
  withoutEdit,
  type SkillEdits,
  type UpdateSkillEdits,
} from '../skillEdits';
import type {
  SkillDoc,
  SkillDraft,
  SkillHistory,
  SkillRefusal,
} from '../types';
import SkillDiff from './SkillDiff';
import SkillVersions from './SkillVersions';

const NOTE =
  "Checks that run in code (citations, rates, round limits) aren't changed by editing this text.";

const SkillEditor = ({
  agent,
  skill,
  client,
  doc,
  history,
  notice,
  onChanged,
  edits,
  onEdits,
}: {
  agent: string;
  skill: string;
  client: AgentClient;
  doc: SkillDoc;
  /** Null when the history couldn't be fetched. */
  history: SkillHistory | null;
  /** A refresh or history failure to show; the editor keeps its text. */
  notice: string | null;
  /** Re-fetch the doc and history; rejects when the fetch failed. */
  onChanged: () => Promise<void>;
  /** Unsaved editor text, held above the Chat/Agents switch (memory only). */
  edits: SkillEdits;
  onEdits: UpdateSkillEdits;
}) => {
  const key = skillKey(agent, skill);
  const baseline = doc.myDraft?.text ?? doc.text;
  const text = editorText(edits, key, baseline);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [reason, setReason] = useState('');
  const [showBehind, setShowBehind] = useState(false);

  // Unsaved text survives a baseline change (it lives in `edits`); once it
  // equals the baseline it isn't unsaved any more, so drop it.
  useEffect(() => {
    onEdits((e) => withEdit(e, key, editorText(e, key, baseline), baseline));
  }, [onEdits, key, baseline]);

  const setText = (next: string) =>
    onEdits((e) => withEdit(e, key, next, baseline));

  const dirty = text !== baseline;
  const behind = draftBehind(doc);
  const buttons = editorButtons(doc, dirty, busy);
  const shownError = errorToShow(error, behind);

  /** Run a write. A write that succeeded counts as done even if the re-fetch after it fails. */
  const run = async (write: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await write();
    } catch (e) {
      const refusal = (e as { refusal?: SkillRefusal }).refusal;
      const extra = refusal ? refusalText(refusal) : '';
      setError([toSafeMessage(e), extra].filter(Boolean).join(' '));
      if (refusal?.code === 'live_moved') {
        setPublishing(false);
        await onChanged().catch(() => undefined);
      }
      setBusy(false);
      return false;
    }
    try {
      await onChanged();
    } catch (e) {
      setError(
        `The change was made, but the skill couldn't be refreshed. ${toSafeMessage(e)}`,
      );
    }
    setBusy(false);
    return true;
  };

  const save = () => {
    const sent = text;
    const refused = localSaveRefusal(sent);
    if (refused) {
      setError(refused);
      return;
    }
    void run(async () => {
      const saved = await client.saveSkillDraft(agent, skill, sent);
      // The service stores its canonical text: adopt it, unless the person kept typing.
      onEdits((e) =>
        withEdit(
          e,
          key,
          afterSave(editorText(e, key, baseline), sent, saved.text),
          baseline,
        ),
      );
    });
  };

  const publish = async () => {
    if (await run(() => client.publishSkill(agent, skill, reason.trim()))) {
      setPublishing(false);
      setReason('');
    }
  };

  const diff = doc.myDraft ? lineDiff(doc.text, doc.myDraft.text) : [];

  return (
    <Flex vertical gap={8} style={{ marginTop: 8 }}>
      {doc.stale && (
        <Alert type="warning" showIcon message={SKILLS_STALE_TEXT} />
      )}
      {notice && !shownError && (
        <Alert type="warning" showIcon message={notice} />
      )}
      {behind && (
        <Alert
          type="warning"
          showIcon
          message={LIVE_MOVED_TEXT}
          description={
            showBehind ? (
              <SkillDiff lines={lineDiff(doc.text, text)} />
            ) : undefined
          }
          action={
            <Button
              size="small"
              type="link"
              aria-expanded={showBehind}
              onClick={() => setShowBehind((v) => !v)}
            >
              {showBehind ? 'Hide the difference' : 'Show the difference'}
            </Button>
          }
        />
      )}
      <Typography.Text type="secondary">{NOTE}</Typography.Text>
      {skillHint(skill) && (
        <Typography.Text type="secondary">{skillHint(skill)}</Typography.Text>
      )}
      <Typography.Text strong>
        {editorSource(doc)}
        {dirty ? ' · unsaved changes' : ''}
      </Typography.Text>
      <Input.TextArea
        value={text}
        autoSize={{ minRows: 6, maxRows: 24 }}
        style={{ fontFamily: 'monospace' }}
        aria-label={`${skill} skill text`}
        onChange={(e) => setText(e.target.value)}
      />
      {shownError && !publishing && (
        <Alert type="error" showIcon message={shownError} />
      )}
      <Flex gap={8} wrap>
        <Button
          type="primary"
          size="small"
          disabled={buttons.save !== 'enabled'}
          loading={busy}
          onClick={save}
        >
          Save draft
        </Button>
        {buttons.discard !== 'hidden' && (
          <Popconfirm
            title="Discard your draft?"
            okText="Discard"
            onConfirm={() =>
              void run(async () => {
                await client.discardSkillDraft(agent, skill);
                onEdits((e) => withoutEdit(e, key));
              })
            }
          >
            <Button size="small" disabled={buttons.discard === 'disabled'}>
              Discard
            </Button>
          </Popconfirm>
        )}
        {buttons.publish !== 'hidden' && (
          <Button
            size="small"
            disabled={buttons.publish === 'disabled'}
            onClick={() => {
              setError(null);
              setReason('');
              setPublishing(true);
            }}
          >
            Publish
          </Button>
        )}
      </Flex>
      <Modal
        open={publishing}
        title="Publish this draft"
        okText="Publish"
        confirmLoading={busy}
        okButtonProps={{ disabled: !oneLineOk(reason) }}
        onOk={publish}
        onCancel={() => setPublishing(false)}
      >
        <SkillDiff lines={diff} />
        <Input
          value={reason}
          maxLength={200}
          placeholder="Why are you publishing this? (required)"
          aria-label="Reason for publishing"
          onChange={(e) => setReason(e.target.value)}
        />
        {error && (
          <Alert
            style={{ marginTop: 8 }}
            type="error"
            showIcon
            message={error}
          />
        )}
      </Modal>
      {history ? (
        <SkillVersions
          history={history}
          drafts={otherDrafts(history, doc)}
          dirty={dirty}
          busy={busy}
          liveText={doc.text}
          copyWarning={copyWarning(doc)}
          onRollback={(version, note) =>
            run(() => client.rollbackSkill(agent, skill, version, note))
          }
          onCopy={(d: SkillDraft) =>
            run(() => client.saveSkillDraft(agent, skill, d.text, d.version))
          }
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Versions couldn't be loaded."
          action={
            <Button
              size="small"
              onClick={() => void onChanged().catch(() => undefined)}
            >
              Retry
            </Button>
          }
        />
      )}
    </Flex>
  );
};

export default SkillEditor;
